import { useState, type ReactNode } from 'react';

export interface ReasonOption {
  value: string;
  label: string;
}

/**
 * DM-6175: some reasons need a second enumerated qualifier before they can be
 * submitted — a moderation REMOVE must name the violation category that the
 * author is told about. Declared here rather than duplicated per page so the
 * "enumerated values only, free text stays in the note" rule holds everywhere.
 */
export interface SecondaryReasonField {
  label: string;
  placeholder: string;
  options: ReasonOption[];
  /** Reason values that require the secondary choice; others hide it. */
  requiredFor: string[];
}

interface ReasonActionBarProps {
  /** Enumerated reasons; free text is only ever the optional note. */
  reasons: ReasonOption[];
  /** Optional second enumerated field, shown only for the reasons that need it. */
  secondary?: SecondaryReasonField;
  /** Confirm button label, e.g. "Suspend user". */
  actionLabel: string;
  /** Tone of the confirm button. */
  destructive?: boolean;
  disabled?: boolean;
  isPending?: boolean;
  notePlaceholder?: string;
  /**
   * DM-7185: deleting an account must always say why in the operator's own
   * words, so the note becomes mandatory and the label says so.
   */
  noteRequired?: boolean;
  /**
   * DM-7185: what the selected reason will actually do, shown under the select
   * before anything is submitted. Announced politely so a screen-reader user
   * hears the consequence change with the choice.
   */
  reasonHint?: (reason: string) => ReactNode;
  /**
   * Prefix for the field ids. Two bars on one page would otherwise share ids
   * and each label would point at the first bar's fields.
   */
  idPrefix?: string;
  onSubmit: (reason: string, note: string | undefined, secondaryValue: string | undefined) => void;
}

const NOTE_MAX_LENGTH = 1000;

/**
 * Reason-required action bar: every privileged action records an enumerated
 * reason (audit-ledger requirement) plus an optional bounded note before the
 * confirm button enables.
 */
export default function ReasonActionBar({
  reasons,
  secondary,
  actionLabel,
  destructive,
  disabled,
  isPending,
  notePlaceholder,
  noteRequired,
  reasonHint,
  idPrefix = 'reason-action',
  onSubmit,
}: ReasonActionBarProps) {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [secondaryValue, setSecondaryValue] = useState('');

  const secondaryRequired = secondary !== undefined && secondary.requiredFor.includes(reason);
  const canSubmit =
    !disabled &&
    !isPending &&
    reason !== '' &&
    note.length <= NOTE_MAX_LENGTH &&
    (!noteRequired || note.trim() !== '') &&
    (!secondaryRequired || secondaryValue !== '');

  return (
    <div className="space-y-3">
      <div>
        <label className="label" htmlFor={`${idPrefix}-reason`}>
          Reason
        </label>
        <select
          id={`${idPrefix}-reason`}
          className="input"
          value={reason}
          onChange={event => setReason(event.target.value)}
        >
          <option value="">Select a reason…</option>
          {reasons.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {reasonHint && <div aria-live="polite">{reasonHint(reason)}</div>}
      </div>
      {secondaryRequired && secondary !== undefined && (
        <div>
          <label className="label" htmlFor={`${idPrefix}-secondary`}>
            {secondary.label}
          </label>
          <select
            id={`${idPrefix}-secondary`}
            className="input"
            value={secondaryValue}
            onChange={event => setSecondaryValue(event.target.value)}
          >
            <option value="">{secondary.placeholder}</option>
            {secondary.options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      )}
      <div>
        <label className="label" htmlFor={`${idPrefix}-note`}>
          {noteRequired ? 'Note (required)' : 'Note (optional)'}
        </label>
        <textarea
          id={`${idPrefix}-note`}
          className="input min-h-[80px]"
          maxLength={NOTE_MAX_LENGTH}
          required={noteRequired}
          placeholder={notePlaceholder ?? 'Additional context for the audit trail'}
          value={note}
          onChange={event => setNote(event.target.value)}
        />
        <p className="text-xs text-gray-text mt-1 text-right">
          {note.length}/{NOTE_MAX_LENGTH}
        </p>
      </div>
      <button
        onClick={() =>
          onSubmit(
            reason,
            note.trim() === '' ? undefined : note.trim(),
            secondaryRequired && secondaryValue !== '' ? secondaryValue : undefined
          )
        }
        disabled={!canSubmit}
        className={`w-full disabled:opacity-50 disabled:cursor-not-allowed ${
          destructive
            ? 'px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white font-medium transition-colors'
            : 'btn-primary'
        }`}
      >
        {isPending ? 'Submitting…' : actionLabel}
      </button>
    </div>
  );
}
